import { useState, useMemo, useEffect } from 'react';
import { Box, Grid, Button, SettingsIcon } from '@forgeglobal/design-framework';
import { Stack, Typography } from '@mui/material';
import { useQuery } from '@tcs/common/hooks/useQuery';
import Spinner from '@tcs/common/components/atoms/Spinner';
import ReportTypeSelector from '@tcs/client-portal-representative/components/molecules/ReportTypeSelector';
import ReportsTable from '@tcs/client-portal-representative/components/molecules/ReportsTable';
import ReportPreferencesModal, {
  ReportPreferences,
} from '@tcs/client-portal-representative/components/molecules/ReportPreferencesModal';
import GenerateReportModal from '@tcs/client-portal-representative/components/molecules/GenerateReportModal';
import useReportPreferences from '@tcs/client-portal-representative/hooks/useReportPreferences';
import useReportPreferencesMutation from '@tcs/client-portal-representative/hooks/useReportPreferencesMutation';
import { ReportType } from '@tcs/client-portal-api/types/ReportType';
import { getDateOnly } from '@tcs/client-portal-common/utils/formatDate';
interface GeneratedReport {
  reportType: string;
  reportPeriod: string;
  dateOfReport: string;
  documentId: string;
  reportName: string;
  startDate: string;
  endDate: string;
  onDemand: boolean;
}

type SortableColumn = 'dateRange' | 'dateGenerated';

const AccountsReports = ({
  representativeCode,
}: {
  representativeCode: string;
}) => {
  const [selectedReportType, setSelectedReportType] = useState(ReportType.ALL);
  const [sortColumn, setSortColumn] = useState<SortableColumn | undefined>(
    'dateGenerated'
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] =
    useState(false);

  const { data: reportPreferences, isLoading: preferencesLoading } =
    useReportPreferences(representativeCode);

  const { data: generatedReports, isLoading: loading } = useQuery<
    GeneratedReport[],
    Error
  >(`/rss/generatedreports/${representativeCode}`, {
    refetchOnMount: 'always',
    enabled: representativeCode !== undefined,
    retry: 3,
  });

  const reportPreferencesMutation =
    useReportPreferencesMutation(representativeCode);

  const [localPreferences, setLocalPreferences] = useState<ReportPreferences>({
    representativeCode,
    accounts: [],
    positions: [],
    transactions: [],
    beneficiary: [],
  });

  const reports = useMemo(() => {
    if (!generatedReports) return [];

    const filteredReports = generatedReports.filter((report) => {
      if (selectedReportType === ReportType.ALL) {
        return true;
      }
      return report.reportType === selectedReportType;
    });

    return filteredReports.map((report) => {
      let reportCreation: string | undefined;

      if (report.onDemand) {
        reportCreation = 'Custom';
      } else {
        reportCreation = 'Scheduled';
      }

      return {
        id: report.documentId,
        dateRange: `${getDateOnly(report.startDate)} - ${getDateOnly(
          report.endDate
        )}`,
        startTimestamp: new Date(report.startDate).getTime(),
        dateGenerated: getDateOnly(report.dateOfReport),
        dateGeneratedTimestamp: new Date(report.dateOfReport).getTime(),
        reportName: report.reportName,
        reportCreation,
        reportType: report.reportType,
        downloadUrl: report.documentId,
      };
    });
  }, [generatedReports, selectedReportType]);

  useEffect(() => {
    if (reportPreferences) {
      setLocalPreferences(reportPreferences);
    }
  }, [reportPreferences]);

  const sortedReports = useMemo(() => {
    if (!sortColumn) return reports;

    return [...reports].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'dateRange') {
        comparison = a.startTimestamp - b.startTimestamp;
      } else if (sortColumn === 'dateGenerated') {
        comparison = a.dateGeneratedTimestamp - b.dateGeneratedTimestamp;
      } else {
        return 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [reports, sortColumn, sortDirection]);

  const handleColumnClick = (
    columnId: string,
    newSortDirection?: 'asc' | 'desc'
  ) => {
    if (['dateRange', 'dateGenerated'].includes(columnId)) {
      setSortColumn(columnId as SortableColumn);
      if (newSortDirection) {
        setSortDirection(newSortDirection);
      }
    }
  };

  const handleOpenPreferences = () => {
    setIsPreferencesModalOpen(true);
  };

  const handleClosePreferences = () => {
    setIsPreferencesModalOpen(false);
  };

  const handleSavePreferences = (preferences: ReportPreferences) => {
    reportPreferencesMutation.mutate(preferences, {
      onSuccess: () => {
        setIsPreferencesModalOpen(false);
        setLocalPreferences(preferences);
      },
    });
  };

  const handleOpenGenerateReport = () => {
    setIsGenerateReportModalOpen(true);
  };

  const handleCloseGenerateReport = () => {
    setIsGenerateReportModalOpen(false);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" fontWeight={600}>
          Reports
        </Typography>
        <Box gap={1} display="flex">
          <Button variant="outlined" onClick={handleOpenGenerateReport}>
            <Typography variant="button" fontWeight={600}>
              Generate a Report
            </Typography>
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={handleOpenPreferences}>
            <Typography variant="button" fontWeight={600}>
              Report Preferences
            </Typography>
          </Button>
        </Box>
      </Stack>

      <Grid container>
        <Grid item xs={12} md={4}>
          <ReportTypeSelector
            selectedReportType={selectedReportType}
            onReportTypeChange={(reportType: ReportType) =>
              setSelectedReportType(reportType)
            }
            includeSelectAllOption
          />
        </Grid>
      </Grid>

      <Typography variant="body2" color="text.secondary">
        Reports are available for download based on type: Weekly, Biweekly, and
        Monthly Reports are kept for 3 months; Quarterly and Annual Reports are
        kept for 6 months from the date they're generated.
      </Typography>

      <ReportsTable reports={sortedReports} onColumnClick={handleColumnClick} />

      <ReportPreferencesModal
        open={isPreferencesModalOpen}
        onClose={handleClosePreferences}
        onSave={handleSavePreferences}
        initialPreferences={localPreferences}
      />

      {isGenerateReportModalOpen && (
        <GenerateReportModal
          open={isGenerateReportModalOpen}
          onClose={handleCloseGenerateReport}
          representativeCode={representativeCode}
        />
      )}
    </Stack>
  );
};

export default AccountsReports;
